import React, { useLayoutEffect, useRef } from 'react';

export type ShellTransitionMode =
    | 'none'
    | 'connecting'
    | 'disconnected'
    | 'error';

interface ShellTransitionPanelProps {
    readonly mode: ShellTransitionMode;
    readonly errorTitle: string;
    readonly onReconnect: () => void;
    readonly onEndSession: () => void;
    readonly onRetry: () => void;
    readonly onReturnToGameLoop: () => void;
    readonly lastSessionId: string;
}

interface DialogAction {
    readonly label: string;
    readonly onClick: () => void;
    readonly disabled?: boolean;
}

interface DialogConfig {
    readonly title: string;
    readonly ariaLabel: string;
    readonly actions: readonly DialogAction[];
}

const CONNECTING_WALLPAPER = 'https://gameloop-mobile-controller.s3.us-east-1.amazonaws.com/assets/img/wallpaper_connecting.jpg';
const TITLE_ID = 'shell-transition-title';

function buildDialogConfig(props: ShellTransitionPanelProps): DialogConfig | null {
    switch (props.mode) {
        case 'error':
            return {
                title: props.errorTitle,
                ariaLabel: 'Stream recovery actions',
                actions: [
                    { label: 'Retry', onClick: props.onRetry },
                    { label: 'Exit', onClick: props.onReturnToGameLoop }
                ]
            };
        case 'disconnected':
            return {
                title: `Session ended or interrupted. Last Session ID: ${props.lastSessionId || '(none)'}`,
                ariaLabel: 'Session end actions',
                actions: [
                    { label: 'Reconnect', onClick: props.onReconnect, disabled: !props.lastSessionId },
                    { label: 'End Game Session', onClick: props.onEndSession }
                ]
            };
        default:
            return null;
    }
}

// TV / D-pad: arrow keys move focus between sibling buttons. Some TV browsers
// do not translate remote arrows into Tab navigation.
function handleDialogKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : event.key === 'ArrowRight' || event.key === 'ArrowDown'
            ? 1
            : 0;
    if (direction === 0) {
        return;
    }

    const target = event.target instanceof HTMLButtonElement ? event.target : null;
    const row = target?.closest<HTMLElement>('.shell-error-actions');
    if (!target || !row) {
        return;
    }

    const buttons = Array.from(row.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    const currentIndex = buttons.indexOf(target);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= buttons.length) {
        return;
    }

    event.preventDefault();
    buttons[nextIndex].focus();
}

function ErrorDialog(props: { readonly config: DialogConfig }): React.ReactElement {
    const { title, ariaLabel, actions } = props.config;
    const actionsRef = useRef<HTMLDivElement>(null);

    // Default focus to the rightmost non-disabled action: on TV without a
    // pointer this lands on the safer/exit option rather than retry.
    useLayoutEffect(() => {
        const enabled = actionsRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
        enabled?.[enabled.length - 1]?.focus();
    }, [title]);

    function handleActionsBlur(event: React.FocusEvent<HTMLDivElement>): void {
        const row = actionsRef.current;
        if (!row) {
            return;
        }
        const next = event.relatedTarget as HTMLElement | null;
        if (next && row.contains(next)) {
            return;
        }
        const blurred = event.target instanceof HTMLButtonElement ? event.target : null;
        window.setTimeout(() => {
            if (blurred && blurred.isConnected && !blurred.disabled) {
                blurred.focus();
                return;
            }
            const enabled = row.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
            enabled[enabled.length - 1]?.focus();
        }, 0);
    }

    return (
        <div className="shell-error-shell">
            <div className="shell-error-card">
                <img className="shell-error-logo" src="/gameloop_dialog_logo.png" alt="" aria-hidden="true" />
                <h1 id={TITLE_ID}>{title}</h1>
                <div
                    ref={actionsRef}
                    className="shell-error-actions"
                    role="group"
                    aria-label={ariaLabel}
                    onBlur={handleActionsBlur}
                >
                    {actions.map((action) => (
                        <button
                            key={action.label}
                            type="button"
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ShellTransitionPanel(props: ShellTransitionPanelProps): React.ReactElement | null {
    if (props.mode === 'none') {
        return null;
    }

    const isConnecting = props.mode === 'connecting';
    const dialogConfig = buildDialogConfig(props);
    const stageClass = isConnecting
        ? 'shell-transition-fixed-stage'
        : 'shell-transition-fixed-stage shell-transition-fixed-stage--solid-error';

    return (
        <div
            className="shell-transition-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogConfig ? TITLE_ID : undefined}
            aria-label={!dialogConfig && isConnecting ? 'Connecting' : undefined}
            aria-live="polite"
            onKeyDown={handleDialogKeyDown}
        >
            <div
                className={stageClass}
                style={isConnecting ? { backgroundImage: `url(${CONNECTING_WALLPAPER})` } : undefined}
            >
                {dialogConfig ? <ErrorDialog config={dialogConfig} /> : null}
            </div>
        </div>
    );
}

export default ShellTransitionPanel;
